import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { PaymentMode, VARIANT_ICONS } from '@checkout/const';
import { PaymentModeService, TimeSlotsService } from '@checkout/feature';
import { BankCard, PaymentModeCodes } from '@payment/const';
import { PaymentService } from '@payment/feature';
import { CUSTOM_ICON_TYPE } from '@shared/models';
import { AuthService } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, skip, switchMap, take } from 'rxjs/operators';

interface SelectedPayment {
  code: string;
  bankCard?: BankCard;
}

@Component({
  selector: 'sd-checkout-payment-choose-list',
  templateUrl: './checkout-payment-choose-list.component.html',
  styleUrls: ['./checkout-payment-choose-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SdvorCheckoutPaymentChooseListComponent implements OnInit, OnDestroy {
  paymentModes$: Observable<PaymentMode[] | undefined>;
  sbrfPaymentMode$: Observable<PaymentMode | undefined>;

  bankCards$: Observable<BankCard[]>;

  activePaymentCode$: Observable<string | undefined>;
  activeBankCard$: Observable<BankCard | undefined>;
  selectedPayment$ = new BehaviorSubject<SelectedPayment | undefined>(undefined);
  isLoggedIn$: Observable<boolean>;
  iconType = CUSTOM_ICON_TYPE;
  PaymentModeCodes = PaymentModeCodes;
  timeSlotsIsEmpty$: Observable<boolean>;
  activeRadioPayment: string | undefined;
  variantIcon: PlainObject = VARIANT_ICONS;

  subscription = new Subscription();

  constructor(
    private paymentModeService: PaymentModeService,
    private paymentService: PaymentService,
    private timeSlotsService: TimeSlotsService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.paymentModes$ = combineLatest([
      this.paymentModeService.getList(),
      this.paymentModeService.getListLoading(),
      this.paymentModeService.getListError(),
    ]).pipe(map(([paymentModes, isLoading, isError]) => (isLoading || isError ? undefined : paymentModes)));

    this.sbrfPaymentMode$ = this.paymentModeService.getPaymentMode(PaymentModeCodes.Sbrf);

    this.timeSlotsIsEmpty$ = this.timeSlotsService.isEmpty();

    this.bankCards$ = this.sbrfPaymentMode$.pipe(
      filter(Boolean),
      switchMap(() => this.paymentService.getPayments()),
    );

    this.activePaymentCode$ = this.paymentModeService.getActivePaymentCode();
    this.activeBankCard$ = this.paymentService.getActivePayment();

    this.isLoggedIn$ = this.authService.isUserLoggedIn();

    this.subscription.add(
      this.selectedPayment$.pipe(skip(1), debounceTime(500)).subscribe(() => {
        this.submitPayment();
      }),
    );
  }

  setActivePayment(code: string, bankCard?: BankCard): void {
    this.selectedPayment$.next({
      code,
      bankCard,
    });
  }

  submitPayment(): void {
    combineLatest([this.activePaymentCode$, this.activeBankCard$])
      .pipe(take(1))
      .subscribe(([activePaymentCode, activeBankCard]: [string, BankCard]) => {
        const { code, bankCard } = this.selectedPayment$.getValue() || {};

        if (activePaymentCode !== code) {
          this.paymentModeService.setActivePaymentMode(code);
        }

        if (bankCard) {
          this.paymentService.setActivePayment(bankCard);
        }

        if (activeBankCard && !bankCard && code === PaymentModeCodes.Sbrf) {
          this.paymentService.resetActivePayment();
        }
      });
  }

  goToPaymentBinding(): void {
    this.paymentModeService.initCardBindingOnExternalAddress();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
